import React, { useState } from "react";
import { buildApiUrl } from "../config/api";
import "./BookUs.css";

const services = [
  {
    number: "01",
    title: "Portrait & Editorial",
    text: "Considered portraits with art direction, natural emotion, and a refined visual language.",
  },
  {
    number: "02",
    title: "Couples & Celebrations",
    text: "Intimate, story-led coverage that preserves atmosphere as beautifully as the occasion.",
  },
  {
    number: "03",
    title: "Brand & Commercial",
    text: "Distinctive imagery for campaigns, hospitality, products, and creative businesses.",
  },
];

const processSteps = [
  ["01", "Tell us your vision", "Share the story, mood, date, and the images you want to walk away with."],
  ["02", "Shape the experience", "We refine the concept, location, timeline, and a collection made for you."],
  ["03", "Create something lasting", "Your session is photographed with care, then edited in our signature style."],
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "",
  date: "",
  location: "",
  budget: "",
  message: "",
};

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function BookUs() {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState("idle");
  const [feedback, setFeedback] = useState("");

  const updateField = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("sending");
    setFeedback("");

    const details = [
      `Service: ${form.service}`,
      form.phone && `Phone: ${form.phone}`,
      form.date && `Preferred date: ${form.date}`,
      form.location && `Location: ${form.location}`,
      form.budget && `Estimated budget: ${form.budget}`,
    ].filter(Boolean);

    try {
      const response = await fetch(buildApiUrl("/contact"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          subject: `${form.service} photography inquiry`,
          message: `${details.join("\n")}\n\nProject vision:\n${form.message.trim()}`,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || "Your inquiry could not be sent.");
      }

      setStatus("success");
      setFeedback("Thank you. Your story is now in our inbox—we'll reply within 1–2 business days.");
      setForm(initialForm);
    } catch (error) {
      setStatus("error");
      setFeedback(error.message || "Something went wrong. Please email us directly.");
    }
  };

  return (
    <main className="mc-book-page">
      <section
        className="mc-book-hero"
        aria-labelledby="booking-title"
        style={{ "--book-hero-image": 'url("/images/mayachitro-golden-river-boat-sunset-hero.jpg")' }}
      >
        <div className="mc-book-hero-image" aria-hidden="true" />
        <div className="mc-book-hero-shade" aria-hidden="true" />
        <div className="mc-book-hero-content">
          <p className="mc-book-kicker">Private commissions · Bangladesh & beyond</p>
          <h1 id="booking-title">Your story,<br /><em>beautifully seen.</em></h1>
          <p className="mc-book-hero-copy">
            Thoughtful photography for people, celebrations, and brands who believe the feeling matters as much as the frame.
          </p>
          <a className="mc-book-primary-link" href="#inquiry">
            Begin your inquiry <ArrowIcon />
          </a>
        </div>
        <div className="mc-book-hero-meta" aria-label="Studio information">
          <span>Based in Dhaka</span>
          <span>Available worldwide</span>
          <span>Est. MayaChitro</span>
        </div>
        <a className="mc-book-scroll" href="#services" aria-label="Explore services">
          <span>Scroll to explore</span><i />
        </a>
      </section>

      <section className="mc-book-intro" id="services">
        <div className="mc-book-section-label"><span>01</span> The experience</div>
        <div className="mc-book-intro-copy">
          <p className="mc-book-kicker">Photography with presence</p>
          <h2>Not simply a shoot.<br />A story made <em>tangible.</em></h2>
          <p>We create honest, elevated imagery with an editorial eye and a human heart. Every commission begins by listening—then we build an experience that feels unmistakably yours.</p>
        </div>
      </section>

      <section className="mc-book-services" aria-label="Photography services">
        {services.map((service) => (
          <article className="mc-book-service" key={service.number}>
            <span>{service.number}</span>
            <div>
              <h3>{service.title}</h3>
              <p>{service.text}</p>
            </div>
            <a href="#inquiry" onClick={() => setForm((current) => ({ ...current, service: service.title }))}>
              Inquire <ArrowIcon />
            </a>
          </article>
        ))}
      </section>

      <section className="mc-book-inquiry" id="inquiry">
        <aside className="mc-book-inquiry-copy">
          <div className="mc-book-section-label"><span>02</span> Start a conversation</div>
          <p className="mc-book-kicker">Now booking select commissions</p>
          <h2>Let’s make<br /><em>something timeless.</em></h2>
          <p>Tell us what you are imagining. The more detail you share, the more thoughtfully we can shape your experience.</p>

          <div className="mc-book-contact-list">
            <a href="mailto:MayaChitro@gmail.com">
              <small>Email</small><strong>MayaChitro@gmail.com</strong>
            </a>
            <a href="tel:+8801776196242">
              <small>Call or WhatsApp</small><strong>+880 1776-196242</strong>
            </a>
            <a href="https://www.instagram.com/maya_chitro/" target="_blank" rel="noopener noreferrer">
              <small>Instagram</small><strong>@maya_chitro</strong>
            </a>
          </div>
        </aside>

        <form className="mc-book-form" onSubmit={handleSubmit}>
          <div className="mc-book-field mc-book-field-full">
            <label htmlFor="book-name">Your name *</label>
            <input id="book-name" name="name" value={form.name} onChange={updateField} placeholder="How should we address you?" required />
          </div>
          <div className="mc-book-field">
            <label htmlFor="book-email">Email address *</label>
            <input id="book-email" name="email" type="email" value={form.email} onChange={updateField} placeholder="you@example.com" required />
          </div>
          <div className="mc-book-field">
            <label htmlFor="book-phone">Phone / WhatsApp</label>
            <input id="book-phone" name="phone" type="tel" value={form.phone} onChange={updateField} placeholder="+880" />
          </div>
          <div className="mc-book-field mc-book-field-full">
            <label htmlFor="book-service">What are you planning? *</label>
            <select id="book-service" name="service" value={form.service} onChange={updateField} required>
              <option value="" disabled>Select a photography service</option>
              {services.map(({ title }) => <option value={title} key={title}>{title}</option>)}
              <option value="Fine art print or image licensing">Fine art print or image licensing</option>
              <option value="Something else">Something else</option>
            </select>
          </div>
          <div className="mc-book-field">
            <label htmlFor="book-date">Preferred date</label>
            <input id="book-date" name="date" type="date" value={form.date} onChange={updateField} />
          </div>
          <div className="mc-book-field">
            <label htmlFor="book-location">Location</label>
            <input id="book-location" name="location" value={form.location} onChange={updateField} placeholder="City or venue" />
          </div>
          <div className="mc-book-field mc-book-field-full">
            <label htmlFor="book-budget">Estimated investment</label>
            <select id="book-budget" name="budget" value={form.budget} onChange={updateField}>
              <option value="">Select a range (optional)</option>
              <option value="Under ৳25,000">Under ৳25,000</option>
              <option value="৳25,000–৳50,000">৳25,000–৳50,000</option>
              <option value="৳50,000–৳100,000">৳50,000–৳100,000</option>
              <option value="৳100,000+">৳100,000+</option>
            </select>
          </div>
          <div className="mc-book-field mc-book-field-full">
            <label htmlFor="book-message">Tell us about your vision *</label>
            <textarea id="book-message" name="message" value={form.message} onChange={updateField} placeholder="The feeling, people, occasion, or story you want us to capture..." rows="5" required />
          </div>
          <div className="mc-book-submit-row mc-book-field-full">
            <button type="submit" disabled={status === "sending"}>
              {status === "sending" ? "Sending inquiry…" : "Send inquiry"}<ArrowIcon />
            </button>
            <p>We typically respond within 1–2 business days.</p>
          </div>
          {feedback && <div className={`mc-book-feedback ${status}`} role="status">{feedback}</div>}
        </form>
      </section>

      <section className="mc-book-process">
        <div className="mc-book-process-heading">
          <div className="mc-book-section-label"><span>03</span> What happens next</div>
          <h2>From first hello<br />to final <em>frame.</em></h2>
        </div>
        <div className="mc-book-process-list">
          {processSteps.map(([number, title, text]) => (
            <article key={number}><span>{number}</span><h3>{title}</h3><p>{text}</p></article>
          ))}
        </div>
      </section>

      <section className="mc-book-final-cta">
        <p className="mc-book-kicker">Have a different kind of project?</p>
        <h2>We are always open to<br /><em>a beautiful idea.</em></h2>
        <a href="mailto:MayaChitro@gmail.com">Write to the studio <ArrowIcon /></a>
      </section>
    </main>
  );
}

export default BookUs;
